import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Mail, ArrowRight, ArrowLeft, RefreshCw, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { forgotPassword } from '@/api/stnApi';

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) { toast.error('Enter your email address'); return; }
    setLoading(true);
    try {
      const res = await forgotPassword(trimmed);
      setEmail(trimmed);
      setSent(true);
      toast.success(res?.message || 'Reset code sent to your email.');
    } catch (err) {
      toast.error(err.message || 'No account found with this email.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="w-full max-w-sm"
      >
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-10">
          <div className="w-9 h-9 rounded-xl bg-violet-600 flex items-center justify-center shadow-lg shadow-violet-900/30">
            <Zap className="w-4.5 h-4.5 text-white" />
          </div>
          <span className="font-extrabold text-foreground text-base tracking-tight">StartupNexus</span>
        </Link>

        <div className="rounded-2xl border border-border bg-card p-7">
          {sent ? (
            <div className="text-center space-y-6">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-7 h-7 text-emerald-500" />
              </div>
              <div className="space-y-1">
                <h1 className="text-xl font-bold text-foreground">Check your inbox</h1>
                <p className="text-sm text-muted-foreground">
                  We sent a 6-digit reset code to{' '}
                  <span className="text-foreground font-medium">{email}</span>. It expires in 10 minutes.
                </p>
              </div>
              <Button
                onClick={() => navigate('/reset-password', { state: { email } })}
                className="w-full gradient-primary text-primary-foreground border-0 h-11 font-semibold gap-2"
              >
                Enter Reset Code <ArrowRight className="w-4 h-4" />
              </Button>
              <button
                onClick={() => setSent(false)}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4 mx-auto">
                  <RefreshCw className="w-5 h-5 text-primary" />
                </div>
                <h1 className="text-2xl font-bold text-foreground tracking-tight">Forgot password?</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Enter your registered email and we'll send you a reset code.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-foreground">Email address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="you@startup.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-10 pl-9"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full gradient-primary text-primary-foreground border-0 h-11 font-semibold gap-2"
                >
                  {loading
                    ? <><RefreshCw className="w-4 h-4 animate-spin" />Sending…</>
                    : <>Send Reset Code <ArrowRight className="w-4 h-4" /></>}
                </Button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-primary hover:underline font-medium">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
